import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import ApiService from '../services/api';

// Default Templates Generator using Template Maker Architecture
const createDefaultTemplates = () => {
  return [
    // 1. Zustand Karosserie
    {
      _id: 'default-karosserie',
      name: '🚗 Zustand Karosserie',
      description: 'Schäden und Zustand an Karosserie - Complete body damage assessment with photo documentation.',
      type: 'Vehicle Inspection',
      category: 'Body & Exterior',
      vehicleSystem: 'Body & Paint',
      inspectionLevel: 'Visual + Photo Documentation',
      language: 'de',
      tags: ['karosserie', 'body', 'damage', 'exterior', 'bodywork'],
      isBuiltIn: true,
      usageCount: 127,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'photo-documentation',
          label: 'Front - Foto',
          component: 'photo-doc',
          required: true,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 2,
          type: 'checkbox',
          label: 'Front - Required',
          component: 'checkbox',
          required: false
        },
        {
          id: 3,
          type: 'photo-documentation',
          label: 'Fahrerseite - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 4,
          type: 'photo-documentation',
          label: 'Beifahrerseite - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 5,
          type: 'photo-documentation',
          label: 'Heck - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 6,
          type: 'photo-documentation',
          label: 'Bodenbereich - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 7,
          type: 'photo-documentation',
          label: 'Dachbereich - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 8,
          type: 'textarea',
          label: 'Bemerkung',
          component: 'textarea',
          required: false,
          rows: 4,
          placeholder: 'Allgemeine Bemerkungen zur Karosserie...'
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 2. Schäden und Zustand Verglasung
    {
      _id: 'default-verglasung',
      name: '🪟 Schäden und Zustand Verglasung',
      description: 'Comprehensive glass inspection covering windshield, side windows, rear window, and mirror glass condition.',
      type: 'Safety Inspection',
      category: 'Glass & Visibility',
      vehicleSystem: 'Glass & Mirrors',
      inspectionLevel: 'Safety Critical',
      language: 'de',
      tags: ['verglasung', 'windshield', 'glass', 'visibility', 'safety'],
      isBuiltIn: true,
      usageCount: 89,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'photo-documentation',
          label: 'Frontscheibe - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 2,
          type: 'photo-documentation',
          label: 'Heckscheibe - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 3,
          type: 'photo-documentation',
          label: 'Seitenscheiben - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 4,
          type: 'photo-documentation',
          label: 'Spiegelglas - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 5,
          type: 'textarea',
          label: 'Bemerkung',
          component: 'textarea',
          required: false,
          rows: 4,
          placeholder: 'Allgemeine Bemerkungen zur Verglasung...'
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 3. Schäden und Zustand Innenraum
    {
      _id: 'default-innenraum',
      name: '🪑 Schäden und Zustand Innenraum',
      description: 'Interior condition assessment including damage inspection, date documentation, and photo capture.',
      type: 'Condition Assessment',
      category: 'Interior & Comfort',
      vehicleSystem: 'Interior Components',
      inspectionLevel: 'Comfort & Functionality',
      language: 'de',
      tags: ['innenraum', 'interior', 'cabin', 'damage', 'inspection'],
      isBuiltIn: true,
      usageCount: 76,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'radio-group',
          label: 'Innenraum auf Schäden geprüft',
          component: 'radio',
          required: true,
          options: ['Ja', 'Nein']
        },
        {
          id: 2,
          type: 'date-input',
          label: 'Datum',
          component: 'date',
          required: false,
          format: 'DD/MM/YYYY'
        },
        {
          id: 3,
          type: 'photo-documentation',
          label: 'Fotos',
          component: 'photo-doc',
          required: false,
          maxPhotos: 5,
          annotations: false
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 4. Schäden und Zustand Reifen und Felgen
    {
      _id: 'default-reifen',
      name: '🛞 Schäden und Zustand Reifen und Felgen',
      description: 'Tire tread depth measurement, wheel condition check, and replacement requirement assessment with legal compliance.',
      type: 'Safety Inspection',
      category: 'Tires & Wheels',
      vehicleSystem: 'Tires & Suspension',
      inspectionLevel: 'Safety Critical + Legal',
      language: 'de',
      tags: ['reifen', 'tires', 'wheels', 'tread', 'safety', 'legal'],
      isBuiltIn: true,
      usageCount: 203,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'measurement',
          label: 'Vorne rechts - Wert in mm',
          component: 'measurement',
          required: false,
          unit: 'mm',
          min: 0,
          max: 20,
          precision: 0.1
        },
        {
          id: 2,
          type: 'photo-documentation',
          label: 'Vorne rechts - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 3,
          type: 'checkbox',
          label: 'Vorne rechts - Required',
          component: 'checkbox',
          required: false
        },
        {
          id: 4,
          type: 'checkbox',
          label: 'Vorne rechts - Reifen neu beschaffen',
          component: 'checkbox',
          required: false
        },
        {
          id: 5,
          type: 'measurement',
          label: 'Vorne links - Wert in mm',
          component: 'measurement',
          required: false,
          unit: 'mm',
          min: 0,
          max: 20,
          precision: 0.1
        },
        {
          id: 6,
          type: 'photo-documentation',
          label: 'Vorne links - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 7,
          type: 'checkbox',
          label: 'Vorne links - Required',
          component: 'checkbox',
          required: false
        },
        {
          id: 8,
          type: 'checkbox',
          label: 'Vorne links - Reifen neu beschaffen',
          component: 'checkbox',
          required: false
        },
        {
          id: 9,
          type: 'measurement',
          label: 'Hinten rechts - Wert in mm',
          component: 'measurement',
          required: false,
          unit: 'mm',
          min: 0,
          max: 20,
          precision: 0.1
        },
        {
          id: 10,
          type: 'photo-documentation',
          label: 'Hinten rechts - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 11,
          type: 'checkbox',
          label: 'Hinten rechts - Required',
          component: 'checkbox',
          required: false
        },
        {
          id: 12,
          type: 'checkbox',
          label: 'Hinten rechts - Reifen neu beschaffen',
          component: 'checkbox',
          required: false
        },
        {
          id: 13,
          type: 'measurement',
          label: 'Hinten links - Wert in mm',
          component: 'measurement',
          required: false,
          unit: 'mm',
          min: 0,
          max: 20,
          precision: 0.1
        },
        {
          id: 14,
          type: 'photo-documentation',
          label: 'Hinten links - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 15,
          type: 'checkbox',
          label: 'Hinten links - Required',
          component: 'checkbox',
          required: false
        },
        {
          id: 16,
          type: 'checkbox',
          label: 'Hinten links - Reifen neu beschaffen',
          component: 'checkbox',
          required: false
        },
        {
          id: 17,
          type: 'measurement',
          label: 'Reserverad - Wert in mm',
          component: 'measurement',
          required: false,
          unit: 'mm',
          min: 0,
          max: 20,
          precision: 0.1
        },
        {
          id: 18,
          type: 'photo-documentation',
          label: 'Reserverad - Foto',
          component: 'photo-doc',
          required: false,
          maxPhotos: 1,
          annotations: false
        },
        {
          id: 19,
          type: 'checkbox',
          label: 'Reserverad - Required',
          component: 'checkbox',
          required: false
        },
        {
          id: 20,
          type: 'checkbox',
          label: 'Reserverad - Reifen neu beschaffen',
          component: 'checkbox',
          required: false
        },
        {
          id: 21,
          type: 'text-input',
          label: 'Reifenhersteller',
          component: 'text',
          required: false,
          placeholder: 'Für Wiederbeschaffung'
        },
        {
          id: 22,
          type: 'text-input',
          label: 'Abmessung',
          component: 'text',
          required: false,
          placeholder: 'Für Wiederbeschaffung'
        },
        {
          id: 23,
          type: 'text-input',
          label: 'Zoll',
          component: 'text',
          required: false,
          placeholder: 'Für Wiederbeschaffung'
        },
        {
          id: 24,
          type: 'text-input',
          label: 'Geschwindigkeitsbuchstabe',
          component: 'text',
          required: false,
          placeholder: 'Für Wiederbeschaffung'
        },
        {
          id: 25,
          type: 'text-input',
          label: 'Tragkraft',
          component: 'text',
          required: false,
          placeholder: 'Für Wiederbeschaffung'
        },
        {
          id: 26,
          type: 'checkbox',
          label: 'Vorhandene Reifengröße ist laut Fahrzeugschein eingetragen',
          component: 'checkbox',
          required: true
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 5. Füllstand vor Service
    {
      _id: 'default-fuellstand',
      name: '🛢️ Füllstand vor Service',
      description: 'Pre-service fluid level inspection including oil levels and service documentation with automatic date/time.',
      type: 'Pre-Service Check',
      category: 'Fluids & Maintenance',
      vehicleSystem: 'Engine & Fluids',
      inspectionLevel: 'Maintenance Critical',
      language: 'de',
      tags: ['füllstand', 'oil', 'fluids', 'maintenance', 'service'],
      isBuiltIn: true,
      usageCount: 156,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'checkbox',
          label: 'Checkbox Required',
          component: 'checkbox',
          required: true
        },
        {
          id: 2,
          type: 'radio-group',
          label: 'Füllstand vor Service: OK',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 3,
          type: 'radio-group',
          label: 'Zu niedrig',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 4,
          type: 'radio-group',
          label: 'Zu hoch',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 5,
          type: 'radio-group',
          label: 'Ölwechsel mit Filter durchgeführt am heutigen Tag',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 6,
          type: 'date-input',
          label: 'Datum (automatisch)',
          component: 'date',
          required: false,
          format: 'DD/MM/YYYY'
        },
        {
          id: 7,
          type: 'time-input',
          label: 'Uhrzeit (automatisch)',
          component: 'time',
          required: false,
          format: '24h'
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 6. Getriebeöl
    {
      _id: 'default-getriebeoil',
      name: '⚙️ Getriebeöl',
      description: 'Transmission oil service with change and filter replacement documentation.',
      type: 'Service Procedure',
      category: 'Transmission & Drivetrain',
      vehicleSystem: 'Transmission',
      inspectionLevel: 'Service Maintenance',
      language: 'de',
      tags: ['getriebeöl', 'transmission', 'oil', 'filter', 'service'],
      isBuiltIn: true,
      usageCount: 94,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'radio-group',
          label: 'Getriebeöl gewechselt',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 2,
          type: 'radio-group',
          label: 'Getriebeölfilter gewechselt',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 7. Steuerkette
    {
      _id: 'default-steuerkette',
      name: '🔗 Steuerkette',
      description: 'Timing chain inspection for elongation check and replacement assessment.',
      type: 'Engine Diagnostics',
      category: 'Engine & Timing',
      vehicleSystem: 'Engine Timing',
      inspectionLevel: 'Technical Diagnostics',
      language: 'de',
      tags: ['steuerkette', 'timing', 'chain', 'engine', 'diagnostics'],
      isBuiltIn: true,
      usageCount: 67,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'radio-group',
          label: 'Auf Längung geprüft',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 2,
          type: 'radio-group',
          label: 'Gewechselt',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 8. Bremsen
    {
      _id: 'default-bremsen',
      name: '🛑 Bremsen',
      description: 'Complete brake system inspection for front and rear brakes including checks and component replacement.',
      type: 'Safety Inspection',
      category: 'Brakes & Safety',
      vehicleSystem: 'Brake System',
      inspectionLevel: 'Safety Critical',
      language: 'de',
      tags: ['bremsen', 'brakes', 'pads', 'discs', 'safety', 'performance'],
      isBuiltIn: true,
      usageCount: 245,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'radio-group',
          label: 'Bremsen vorne - Geprüft',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 2,
          type: 'radio-group',
          label: 'Bremsen vorne - Klötze gewechselt',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 3,
          type: 'radio-group',
          label: 'Bremsen vorne - Scheiben gewechselt',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 4,
          type: 'radio-group',
          label: 'Bremsen hinten - Geprüft',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 5,
          type: 'radio-group',
          label: 'Bremsen hinten - Klötze gewechselt',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 6,
          type: 'radio-group',
          label: 'Bremsen hinten - Scheiben gewechselt',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    },
    
    // 9. Beleuchtung
    {
      _id: 'default-beleuchtung',
      name: '💡 Beleuchtung',
      description: 'Lighting system inspection covering functionality checks and maintenance documentation.',
      type: 'Electrical Inspection',
      category: 'Lighting & Electrical',
      vehicleSystem: 'Electrical & Lighting',
      inspectionLevel: 'Safety & Legal',
      language: 'de',
      tags: ['beleuchtung', 'lights', 'electrical', 'headlights', 'safety'],
      isBuiltIn: true,
      usageCount: 134,
      templateKey: 'default-template',
      formFields: [
        {
          id: 1,
          type: 'radio-group',
          label: 'Geprüft',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 2,
          type: 'radio-group',
          label: 'Anhängerdose geprüft',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 3,
          type: 'radio-group',
          label: 'Innenbeleuchtung geprüft',
          component: 'radio',
          required: false,
          options: ['Ja', 'Nein']
        },
        {
          id: 4,
          type: 'textarea',
          label: 'Folgendes in Stand gesetzt',
          component: 'textarea',
          required: false,
          rows: 4,
          placeholder: 'Beschreibung der durchgeführten Arbeiten...'
        }
      ],
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];
};
// Import the unified template renderer
// Old individual templates are no longer needed

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState(['all']);
  const [types, setTypes] = useState(['all']);
  const [languages, setLanguages] = useState(['all']);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchTemplateOptions();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchQuery, selectedCategory, selectedType, selectedLanguage]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      console.log('Fetching templates...');
      const response = await ApiService.getTemplates();
      console.log('Templates response:', response);
      if (response.success) {
        console.log('Templates found:', response.templates);
        setTemplates(response.templates);
      } else {
        console.log('No templates found or failed response:', response);
        // Create default templates using template maker architecture
        const defaultTemplates = createDefaultTemplates();
        setTemplates(defaultTemplates);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      // Show default templates even on error for demo
      const defaultTemplates = createDefaultTemplates();
      setTemplates(defaultTemplates);
      setError(`API Error: ${err.message} (showing demo template)`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateOptions = async () => {
    try {
      const response = await ApiService.getTemplateOptions();
      if (response.success) {
        setCategories(['all', ...response.options.categories]);
        setTypes(['all', ...response.options.types]);
        setLanguages(['all', ...response.options.languages]);
      }
    } catch (err) {
      console.error('Error fetching template options:', err);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(template => template.category === selectedCategory);
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(template => template.type === selectedType);
    }

    // Language filter
    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(template => template.language === selectedLanguage);
    }

    setFilteredTemplates(filtered);
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const newName = prompt(`Enter name for duplicated template:`, `${template.name} (Copy)`);
      if (newName) {
        await ApiService.duplicateTemplate(template._id, newName);
        fetchTemplates();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      try {
        await ApiService.deleteTemplate(template._id);
        fetchTemplates();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewTemplate(null);
  };

  const handleTemplateSubmit = (formData) => {
    console.log('Template form data:', formData);
    alert('This is a preview - form data logged to console');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Vehicle Inspection':
        return '🔍';
      case 'Safety Inspection':
        return '🛡️';
      case 'Pre-Service Check':
        return '✅';
      case 'Service Procedure':
        return '🔧';
      case 'Engine Diagnostics':
        return '🔬';
      case 'Electrical Inspection':
        return '⚡';
      case 'Condition Assessment':
        return '📋';
      default:
        return '🚗';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Vehicle Inspection':
        return 'bg-blue-900 text-blue-100';
      case 'Safety Inspection':
        return 'bg-red-900 text-red-100';
      case 'Pre-Service Check':
        return 'bg-green-900 text-green-100';
      case 'Service Procedure':
        return 'bg-orange-900 text-orange-100';
      case 'Engine Diagnostics':
        return 'bg-purple-900 text-purple-100';
      case 'Electrical Inspection':
        return 'bg-yellow-900 text-yellow-100';
      case 'Condition Assessment':
        return 'bg-indigo-900 text-indigo-100';
      default:
        return 'bg-gray-900 text-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-dark-300 mt-4">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Task Templates</h1>
          <p className="text-dark-300 mt-2">
            Manage and use pre-built task templates for common workflows
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <FunnelIcon className="h-4 w-4" />
            <span>Filters</span>
          </button>
          <button 
            onClick={() => setShowCustomBuilder(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>🚗 Create Vehicle Template</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10 w-full"
          />
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="form-input w-full"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="form-input w-full"
              >
                {types.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Language</label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="form-input w-full"
              >
                {languages.map(language => (
                  <option key={language} value={language}>
                    {language === 'all' ? 'All Languages' : language.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          <DocumentTextIcon className="h-24 w-24 mx-auto text-dark-400 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No templates found</h3>
          <p className="text-dark-300 mb-6">
            {searchQuery || selectedCategory !== 'all' || selectedType !== 'all' || selectedLanguage !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first template to get started'
            }
          </p>
          <button className="btn btn-primary">
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template._id}
              className="bg-dark-800 border border-dark-700 rounded-lg p-6 hover:bg-dark-700 transition-colors"
            >
              {/* Template Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="text-2xl mr-3">
                    {getTypeIcon(template.type)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {template.name}
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(template.type)}`}>
                        {template.type}
                      </span>
                      <span className="text-xs text-dark-400">
                        {template.language.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                {template.isBuiltIn && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                    Built-in
                  </span>
                )}
              </div>

              {/* Template Description */}
              <p className="text-dark-300 text-sm mb-4 line-clamp-2">
                {template.description}
              </p>

              {/* Template Tags */}
              {template.tags && template.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {template.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-dark-700 text-dark-300"
                    >
                      #{tag}
                    </span>
                  ))}
                  {template.tags.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-dark-700 text-dark-300">
                      +{template.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Vehicle System Info */}
              {template.vehicleSystem && (
                <div className="mb-3">
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-900 text-primary-100">
                    🔧 {template.vehicleSystem}
                  </span>
                </div>
              )}
              
              {/* Inspection Level */}
              {template.inspectionLevel && (
                <div className="mb-3">
                  <span className="text-xs text-dark-400">Level: </span>
                  <span className="text-xs text-primary-400 font-medium">{template.inspectionLevel}</span>
                </div>
              )}

              {/* Template Stats */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center text-sm text-dark-400">
                  <ChartBarIcon className="h-4 w-4 mr-1" />
                  <span>{template.usageCount || 0} uses</span>
                </div>
                <div className="text-sm text-green-400 font-medium">
                  ✓ Professional
                </div>
              </div>

              {/* Template Actions */}
              <div className="flex justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePreviewTemplate(template)}
                    className="p-2 text-dark-400 hover:text-white hover:bg-dark-600 rounded transition-colors"
                    title="Preview"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDuplicateTemplate(template)}
                    className="p-2 text-dark-400 hover:text-white hover:bg-dark-600 rounded transition-colors"
                    title="Duplicate"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </button>
                  {!template.isBuiltIn && (
                    <>
                      <button
                        onClick={() => alert('Edit not implemented yet')}
                        className="p-2 text-dark-400 hover:text-white hover:bg-dark-600 rounded transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-dark-600 rounded transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setActiveTemplate(template)}
                  className="btn btn-primary btn-sm"
                >
                  Use Template
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-black opacity-75" onClick={handleClosePreview}></div>
            </div>

            <div className="inline-block align-bottom bg-dark-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full">
              <div className="bg-dark-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-white">
                      Template Preview: {previewTemplate.name}
                    </h3>
                    <p className="text-sm text-dark-300 mt-1">
                      {previewTemplate.description}
                    </p>
                  </div>
                  <button
                    onClick={handleClosePreview}
                    className="text-gray-400 hover:text-white focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Template Content */}
                <div className="max-h-96 overflow-y-auto">
                  <div className="space-y-6">
                      <div className="card p-6">
                        <h4 className="text-lg font-semibold text-white mb-4">Template Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-dark-300">Type:</span>
                            <span className="ml-2 text-white capitalize">{previewTemplate.type}</span>
                          </div>
                          <div>
                            <span className="text-dark-300">Category:</span>
                            <span className="ml-2 text-white capitalize">{previewTemplate.category}</span>
                          </div>
                          <div>
                            <span className="text-dark-300">Language:</span>
                            <span className="ml-2 text-white uppercase">{previewTemplate.language}</span>
                          </div>
                          <div>
                            <span className="text-dark-300">Sections:</span>
                            <span className="ml-2 text-white">{previewTemplate.sections?.length || 0}</span>
                          </div>
                        </div>
                        
                        {previewTemplate.tags && previewTemplate.tags.length > 0 && (
                          <div className="mt-4">
                            <span className="text-dark-300 text-sm">Tags:</span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {previewTemplate.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-primary-600 text-white"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {previewTemplate.sections && previewTemplate.sections.length > 0 && (
                        <div className="card p-6">
                          <h4 className="text-lg font-semibold text-white mb-4">Template Sections</h4>
                          <div className="space-y-4">
                            {previewTemplate.sections.map((section, index) => (
                              <div key={index} className="border border-dark-600 rounded-lg p-4">
                                <h5 className="font-medium text-primary-400 mb-2">{section.name}</h5>
                                {section.description && (
                                  <p className="text-dark-300 text-sm mb-3">{section.description}</p>
                                )}
                                {section.fields && section.fields.length > 0 && (
                                  <div className="space-y-2">
                                    <span className="text-dark-300 text-sm font-medium">Fields:</span>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {section.fields.map((field, fieldIndex) => (
                                        <div key={fieldIndex} className="text-sm">
                                          <span className="text-white">{field.label}</span>
                                          <span className="text-dark-400 ml-2">({field.fieldType})</span>
                                          {field.required && (
                                            <span className="text-red-400 ml-1">*</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                </div>
              </div>
              
              <div className="bg-dark-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleClosePreview}
                  className="btn btn-secondary w-full sm:w-auto"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unified Template Renderer */}
      {activeTemplate && (
        <UnifiedTemplateRenderer
          template={activeTemplate}
          open={true}
          onClose={() => setActiveTemplate(null)}
          onSubmit={handleTemplateSubmit}
        />
      )}

      {/* Custom Template Builder Modal */}
      {showCustomBuilder && (
        <CustomTemplateBuilder
          open={showCustomBuilder}
          onClose={() => setShowCustomBuilder(false)}
          onSave={(templateData) => {
            console.log('New custom template:', templateData);
            alert('Custom template created! (This is a demo)');
            setShowCustomBuilder(false);
            fetchTemplates(); // Refresh templates
          }}
        />
      )}
    </div>
  );
};

// Professional Vehicle Template Builder Component
const CustomTemplateBuilder = ({ open, onClose, onSave }) => {
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateType, setTemplateType] = useState('Vehicle Inspection');
  const [templateCategory, setTemplateCategory] = useState('Body & Exterior');
  const [vehicleSystem, setVehicleSystem] = useState('Engine & Drivetrain');
  const [inspectionLevel, setInspectionLevel] = useState('Safety Critical');
  const [templateTags, setTemplateTags] = useState('');
  const [formFields, setFormFields] = useState([]);
  const [draggedField, setDraggedField] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  // Vehicle-specific template types
  const vehicleTemplateTypes = [
    { value: 'Vehicle Inspection', label: '🔍 Vehicle Inspection', icon: '🔍' },
    { value: 'Safety Inspection', label: '🛡️ Safety Inspection', icon: '🛡️' },
    { value: 'Pre-Service Check', label: '✅ Pre-Service Check', icon: '✅' },
    { value: 'Service Procedure', label: '🔧 Service Procedure', icon: '🔧' },
    { value: 'Engine Diagnostics', label: '🔬 Engine Diagnostics', icon: '🔬' },
    { value: 'Electrical Inspection', label: '⚡ Electrical Inspection', icon: '⚡' },
    { value: 'Condition Assessment', label: '📋 Condition Assessment', icon: '📋' }
  ];

  // Vehicle system categories
  const vehicleCategories = [
    { value: 'Body & Exterior', label: '🚗 Body & Exterior', systems: ['Body Panels', 'Paint & Finish', 'Bumpers', 'Doors & Windows'] },
    { value: 'Engine & Drivetrain', label: '⚙️ Engine & Drivetrain', systems: ['Engine Block', 'Transmission', 'Exhaust System', 'Fuel System'] },
    { value: 'Brakes & Safety', label: '🛑 Brakes & Safety', systems: ['Brake Pads', 'Brake Discs', 'ABS System', 'Safety Features'] },
    { value: 'Suspension & Steering', label: '🛣️ Suspension & Steering', systems: ['Shock Absorbers', 'Springs', 'Steering Rack', 'Wheel Alignment'] },
    { value: 'Electrical & Electronics', label: '⚡ Electrical & Electronics', systems: ['Battery', 'Alternator', 'ECU', 'Wiring Harness'] },
    { value: 'Tires & Wheels', label: '🛞 Tires & Wheels', systems: ['Tire Tread', 'Wheel Rims', 'Tire Pressure', 'Wheel Bearings'] },
    { value: 'Interior & Comfort', label: '🪑 Interior & Comfort', systems: ['Seats', 'Dashboard', 'Climate Control', 'Infotainment'] },
    { value: 'Glass & Visibility', label: '🪟 Glass & Visibility', systems: ['Windshield', 'Side Windows', 'Mirrors', 'Wipers'] },
    { value: 'Fluids & Maintenance', label: '🛢️ Fluids & Maintenance', systems: ['Engine Oil', 'Coolant', 'Brake Fluid', 'Power Steering'] }
  ];

  // Vehicle systems
  const vehicleSystems = [
    'Engine & Drivetrain', 'Brake System', 'Suspension System', 'Electrical System',
    'Body & Paint', 'Interior Components', 'Glass & Mirrors', 'Tires & Wheels',
    'Exhaust System', 'Fuel System', 'Cooling System', 'Air Conditioning',
    'Safety Systems', 'Lighting System', 'Engine Timing', 'Transmission'
  ];

  // Inspection levels
  const inspectionLevels = [
    'Safety Critical', 'Legal Requirement', 'Maintenance Critical', 'Performance Check',
    'Comfort & Functionality', 'Visual Inspection', 'Technical Diagnostics', 'Service Maintenance'
  ];

  // Professional vehicle field types
  const vehicleFieldTypes = [
    // Measurement Fields
    { 
      id: 'measurement', 
      label: 'Measurement Field', 
      icon: '📏', 
      description: 'Numeric measurement with units',
      component: 'measurement',
      defaultProps: { unit: 'mm', min: 0, max: 100, precision: 1 }
    },
    { 
      id: 'condition-rating', 
      label: 'Condition Rating', 
      icon: '⭐', 
      description: '5-star condition rating',
      component: 'rating',
      defaultProps: { max: 5, labels: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'] }
    },
    // Safety & Inspection
    { 
      id: 'safety-check', 
      label: 'Safety Check', 
      icon: '🛡️', 
      description: 'Pass/Fail/Warning safety check',
      component: 'safety-check',
      defaultProps: { options: ['Pass', 'Fail', 'Warning'] }
    },
    { 
      id: 'photo-documentation', 
      label: 'Photo Documentation', 
      icon: '📷', 
      description: 'Photo capture with annotations',
      component: 'photo-doc',
      defaultProps: { maxPhotos: 5, annotations: true }
    },
    // Vehicle Specific
    { 
      id: 'tire-depth', 
      label: 'Tire Tread Depth', 
      icon: '🛞', 
      description: 'Tire tread depth measurement',
      component: 'tire-depth',
      defaultProps: { positions: ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'], unit: 'mm' }
    },
    { 
      id: 'fluid-level', 
      label: 'Fluid Level Check', 
      icon: '🛢️', 
      description: 'Fluid level with OK/Low/High',
      component: 'fluid-level',
      defaultProps: { levels: ['OK', 'Low', 'High', 'Empty'] }
    },
    { 
      id: 'damage-map', 
      label: 'Damage Mapping', 
      icon: '🗺️', 
      description: 'Visual damage marking on vehicle diagram',
      component: 'damage-map',
      defaultProps: { vehicleType: 'car' }
    },
    // Standard Fields
    { 
      id: 'text-input', 
      label: 'Text Input', 
      icon: '📝', 
      description: 'Single line text input',
      component: 'text',
      defaultProps: { placeholder: 'Enter text...' }
    },
    { 
      id: 'textarea', 
      label: 'Text Area', 
      icon: '📄', 
      description: 'Multi-line text input',
      component: 'textarea',
      defaultProps: { rows: 3, placeholder: 'Enter detailed notes...' }
    },
    { 
      id: 'checkbox', 
      label: 'Checkbox', 
      icon: '☑️', 
      description: 'Single checkbox option',
      component: 'checkbox',
      defaultProps: { label: 'Check if applicable' }
    },
    { 
      id: 'radio-group', 
      label: 'Radio Buttons', 
      icon: '🔘', 
      description: 'Single choice from options',
      component: 'radio',
      defaultProps: { options: ['Yes', 'No', 'N/A'] }
    },
    { 
      id: 'select', 
      label: 'Dropdown Select', 
      icon: '📆', 
      description: 'Dropdown selection',
      component: 'select',
      defaultProps: { options: ['Option 1', 'Option 2', 'Option 3'] }
    },
    { 
      id: 'date-input', 
      label: 'Date Input', 
      icon: '📅', 
      description: 'Date picker',
      component: 'date',
      defaultProps: { format: 'DD/MM/YYYY' }
    },
    { 
      id: 'time-input', 
      label: 'Time Input', 
      icon: '🕰️', 
      description: 'Time picker',
      component: 'time',
      defaultProps: { format: '24h' }
    },
    { 
      id: 'signature', 
      label: 'Digital Signature', 
      icon: '✍️', 
      description: 'Digital signature capture',
      component: 'signature',
      defaultProps: { required: true }
    }
  ];

  // Drag and drop handlers
  const handleDragStart = (e, fieldType) => {
    setDraggedField(fieldType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedField) {
      const newField = {
        id: Date.now(),
        type: draggedField.id,
        label: `${draggedField.label} ${formFields.length + 1}`,
        component: draggedField.component,
        required: false,
        ...draggedField.defaultProps
      };
      setFormFields([...formFields, newField]);
      setDraggedField(null);
    }
  };

  const handleRemoveField = (fieldId) => {
    setFormFields(formFields.filter(field => field.id !== fieldId));
  };

  const handleFieldUpdate = (fieldId, updates) => {
    setFormFields(formFields.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    ));
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (formFields.length === 0) {
      alert('Please add at least one field to your template');
      return;
    }

    const templateData = {
      _id: Date.now().toString(),
      name: `🚗 ${templateName}`,
      description: templateDescription,
      type: templateType,
      category: templateCategory,
      vehicleSystem: vehicleSystem,
      inspectionLevel: inspectionLevel,
      language: 'de',
      tags: templateTags.split(',').map(tag => tag.trim()).filter(tag => tag),
      formFields: formFields,
      isBuiltIn: false,
      usageCount: 0,
      templateKey: 'custom-' + Date.now(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    onSave(templateData);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-black opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-dark-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-dark-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg leading-6 font-medium text-white">
                Create Custom Template
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="max-h-[600px] overflow-y-auto space-y-6">
              {/* Template Configuration */}
              <div className="card p-6 border-l-4 border-l-primary-500">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-white flex items-center">
                    🚗 Vehicle Template Configuration
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPreviewMode(!previewMode)}
                      className={`btn btn-sm ${previewMode ? 'btn-primary' : 'btn-secondary'}`}
                    >
                      {previewMode ? '📝 Edit' : '👁️ Preview'}
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="form-label flex items-center">
                        🏷️ Template Name *
                      </label>
                      <input
                        type="text"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        className="form-input w-full"
                        placeholder="e.g., Engine Oil Change Inspection"
                      />
                    </div>
                    
                    <div>
                      <label className="form-label flex items-center">
                        🔍 Inspection Type
                      </label>
                      <select
                        value={templateType}
                        onChange={(e) => setTemplateType(e.target.value)}
                        className="form-input w-full"
                      >
                        {vehicleTemplateTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="form-label flex items-center">
                        🔧 Vehicle System
                      </label>
                      <select
                        value={vehicleSystem}
                        onChange={(e) => setVehicleSystem(e.target.value)}
                        className="form-input w-full"
                      >
                        {vehicleSystems.map(system => (
                          <option key={system} value={system}>{system}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="form-label flex items-center">
                        📋 Category
                      </label>
                      <select
                        value={templateCategory}
                        onChange={(e) => setTemplateCategory(e.target.value)}
                        className="form-input w-full"
                      >
                        {vehicleCategories.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="form-label flex items-center">
                        ⚙️ Inspection Level
                      </label>
                      <select
                        value={inspectionLevel}
                        onChange={(e) => setInspectionLevel(e.target.value)}
                        className="form-input w-full"
                      >
                        {inspectionLevels.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="form-label flex items-center">
                        🏷️ Tags (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={templateTags}
                        onChange={(e) => setTemplateTags(e.target.value)}
                        className="form-input w-full"
                        placeholder="brake pads, safety, inspection"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <label className="form-label flex items-center">
                    📄 Description
                  </label>
                  <textarea
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    className="form-input w-full"
                    rows={3}
                    placeholder="Describe the purpose and scope of this vehicle inspection template"
                  />
                </div>
              </div>

              {/* Template Builder */}
              {!previewMode ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Field Palette */}
                  <div className="lg:col-span-1">
                    <div className="card p-4 sticky top-4">
                      <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                        🧩 Field Components
                      </h4>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {vehicleFieldTypes.map((fieldType) => (
                          <div
                            key={fieldType.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, fieldType)}
                            className="p-3 border border-dark-600 rounded-lg cursor-grab hover:bg-dark-700 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">{fieldType.icon}</span>
                                <div>
                                  <div className="text-sm font-medium text-white">
                                    {fieldType.label}
                                  </div>
                                  <div className="text-xs text-dark-300">
                                    {fieldType.description}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs text-primary-400">
                                Drag →
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Form Builder */}
                  <div className="lg:col-span-2">
                    <div className="card p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-semibold text-white flex items-center">
                          🗺️ Form Layout
                        </h4>
                        <div className="text-sm text-dark-300">
                          {formFields.length} field{formFields.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      
                      <div
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        className="min-h-96 border-2 border-dashed border-dark-600 rounded-lg p-6 space-y-4"
                      >
                        {formFields.length === 0 ? (
                          <div className="text-center py-12">
                            <div className="text-6xl mb-4">🚗</div>
                            <h3 className="text-lg font-medium text-white mb-2">
                              Drag & Drop Fields Here
                            </h3>
                            <p className="text-dark-300">
                              Drag field components from the left panel to build your vehicle inspection template
                            </p>
                          </div>
                        ) : (
                          formFields.map((field, index) => (
                            <FieldEditor
                              key={field.id}
                              field={field}
                              index={index}
                              onUpdate={(updates) => handleFieldUpdate(field.id, updates)}
                              onRemove={() => handleRemoveField(field.id)}
                            />
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Template Preview */
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    👁️ Template Preview
                  </h4>
                  <div className="border border-dark-600 rounded-lg p-6 bg-dark-900">
                    <div className="mb-6">
                      <h2 className="text-xl font-bold text-white mb-2">
                        🚗 {templateName || 'Untitled Template'}
                      </h2>
                      <p className="text-dark-300">{templateDescription}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="px-2 py-1 bg-primary-900 text-primary-100 rounded text-xs">
                          {templateType}
                        </span>
                        <span className="px-2 py-1 bg-green-900 text-green-100 rounded text-xs">
                          {vehicleSystem}
                        </span>
                        <span className="px-2 py-1 bg-blue-900 text-blue-100 rounded text-xs">
                          {inspectionLevel}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {formFields.map((field, index) => (
                        <FieldPreview key={field.id} field={field} index={index} />
                      ))}
                      
                      {formFields.length === 0 && (
                        <div className="text-center py-8 text-dark-400">
                          No fields added yet. Switch to Edit mode to add fields.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}


            </div>
          </div>
          
          <div className="bg-dark-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSaveTemplate}
              className="btn btn-primary w-full sm:w-auto sm:ml-3"
              disabled={!templateName.trim() || formFields.length === 0}
            >
              Save Template
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary mt-3 w-full sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Field Editor Component
const FieldEditor = ({ field, index, onUpdate, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-dark-600 rounded-lg p-4 bg-dark-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 bg-primary-600 rounded text-white text-xs flex items-center justify-center font-bold">
            {index + 1}
          </div>
          <div>
            <div className="font-medium text-white">{field.label}</div>
            <div className="text-xs text-dark-300">
              Type: {field.fieldType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-dark-400 hover:text-white"
          >
            {isExpanded ? '⬆️' : '⬇️'}
          </button>
          <button
            onClick={onRemove}
            className="p-1 text-red-400 hover:text-red-300"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="mt-4 space-y-3 border-t border-dark-600 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="form-label text-xs">Field Label</label>
              <input
                type="text"
                value={field.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                className="form-input w-full text-sm"
                placeholder="Enter field label"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
                className="rounded mr-2"
              />
              <label className="text-sm text-white">Required field</label>
            </div>
          </div>
          
          {/* Field-specific properties */}
          {field.fieldType === 'measurement' && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="form-label text-xs">Unit</label>
                <select
                  value={field.unit}
                  onChange={(e) => onUpdate({ unit: e.target.value })}
                  className="form-input w-full text-sm"
                >
                  <option value="mm">mm</option>
                  <option value="cm">cm</option>
                  <option value="m">m</option>
                  <option value="kg">kg</option>
                  <option value="l">l</option>
                  <option value="bar">bar</option>
                  <option value="°C">°C</option>
                </select>
              </div>
              <div>
                <label className="form-label text-xs">Min Value</label>
                <input
                  type="number"
                  value={field.min}
                  onChange={(e) => onUpdate({ min: Number(e.target.value) })}
                  className="form-input w-full text-sm"
                />
              </div>
              <div>
                <label className="form-label text-xs">Max Value</label>
                <input
                  type="number"
                  value={field.max}
                  onChange={(e) => onUpdate({ max: Number(e.target.value) })}
                  className="form-input w-full text-sm"
                />
              </div>
            </div>
          )}
          
          {(field.fieldType === 'radio-group' || field.fieldType === 'select') && (
            <div>
              <label className="form-label text-xs">Options (one per line)</label>
              <textarea
                value={field.options?.join('\n') || ''}
                onChange={(e) => onUpdate({ options: e.target.value.split('\n').filter(opt => opt.trim()) })}
                className="form-input w-full text-sm"
                rows={3}
                placeholder="Option 1\nOption 2\nOption 3"
              />
            </div>
          )}
          
          {field.fieldType === 'safety-check' && (
            <div>
              <label className="form-label text-xs">Safety Options</label>
              <div className="text-xs text-dark-300">
                Pass ✅ | Fail ❌ | Warning ⚠️
              </div>
            </div>
          )}
          
          {field.fieldType === 'tire-depth' && (
            <div>
              <label className="form-label text-xs">Tire Positions</label>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-center p-2 bg-dark-700 rounded">Front Left</div>
                <div className="text-center p-2 bg-dark-700 rounded">Front Right</div>
                <div className="text-center p-2 bg-dark-700 rounded">Rear Left</div>
                <div className="text-center p-2 bg-dark-700 rounded">Rear Right</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Field Preview Component
const FieldPreview = ({ field, index }) => {
  const renderFieldPreview = () => {
    switch (field.fieldType) {
      case 'measurement':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="number"
              placeholder="0"
              className="form-input w-24"
              disabled
            />
            <span className="text-dark-300">{field.unit}</span>
            <span className="text-xs text-dark-400">({field.min}-{field.max})</span>
          </div>
        );
        
      case 'condition-rating':
        return (
          <div className="flex space-x-1">
            {[1,2,3,4,5].map(star => (
              <span key={star} className="text-yellow-400 text-xl">⭐</span>
            ))}
          </div>
        );
        
      case 'safety-check':
        return (
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2">
              <input type="radio" name={`safety-${field.id}`} disabled />
              <span className="text-green-400">✅ Pass</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="radio" name={`safety-${field.id}`} disabled />
              <span className="text-red-400">❌ Fail</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="radio" name={`safety-${field.id}`} disabled />
              <span className="text-yellow-400">⚠️ Warning</span>
            </label>
          </div>
        );
        
      case 'photo-documentation':
        return (
          <div className="border-2 border-dashed border-dark-600 rounded-lg p-4 text-center">
            <div className="text-2xl mb-2">📷</div>
            <div className="text-sm text-dark-300">Click to capture photo</div>
          </div>
        );
        
      case 'tire-depth':
        return (
          <div className="grid grid-cols-2 gap-3">
            {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map(position => (
              <div key={position} className="space-y-1">
                <label className="text-xs text-dark-300">{position}</label>
                <div className="flex items-center space-x-2">
                  <input type="number" className="form-input w-16 text-sm" placeholder="0" disabled />
                  <span className="text-xs text-dark-400">mm</span>
                </div>
              </div>
            ))}
          </div>
        );
        
      case 'fluid-level':
        return (
          <div className="flex space-x-4">
            {['OK', 'Low', 'High', 'Empty'].map(level => (
              <label key={level} className="flex items-center space-x-2">
                <input type="radio" name={`fluid-${field.id}`} disabled />
                <span className={`text-sm ${
                  level === 'OK' ? 'text-green-400' : 
                  level === 'Low' ? 'text-yellow-400' :
                  level === 'High' ? 'text-orange-400' : 'text-red-400'
                }`}>{level}</span>
              </label>
            ))}
          </div>
        );
        
      case 'damage-map':
        return (
          <div className="border border-dark-600 rounded-lg p-4 bg-dark-700">
            <div className="text-center">
              <div className="text-4xl mb-2">🚗</div>
              <div className="text-sm text-dark-300">Interactive damage mapping</div>
            </div>
          </div>
        );
        
      case 'textarea':
        return (
          <textarea
            className="form-input w-full"
            rows={field.rows || 3}
            placeholder={field.placeholder}
            disabled
          />
        );
        
      case 'checkbox':
        return (
          <label className="flex items-center space-x-2">
            <input type="checkbox" disabled />
            <span>{field.label}</span>
          </label>
        );
        
      case 'radio-group':
        return (
          <div className="space-y-2">
            {field.options?.map((option, idx) => (
              <label key={idx} className="flex items-center space-x-2">
                <input type="radio" name={`radio-${field.id}`} disabled />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
        
      case 'select':
        return (
          <select className="form-input w-full" disabled>
            <option>Select an option...</option>
            {field.options?.map((option, idx) => (
              <option key={idx}>{option}</option>
            ))}
          </select>
        );
        
      case 'date-input':
        return <input type="date" className="form-input w-full" disabled />;
        
      case 'time-input':
        return <input type="time" className="form-input w-full" disabled />;
        
      case 'signature':
        return (
          <div className="border border-dark-600 rounded-lg p-4 bg-dark-700 text-center">
            <div className="text-2xl mb-2">✍️</div>
            <div className="text-sm text-dark-300">Digital signature area</div>
          </div>
        );
        
      default:
        return (
          <input
            type="text"
            className="form-input w-full"
            placeholder={field.placeholder || 'Enter text...'}
            disabled
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      <label className="form-label flex items-center">
        {field.label}
        {field.required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {renderFieldPreview()}
    </div>
  );
};

// Unified Template Renderer Component
const UnifiedTemplateRenderer = ({ template, open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({});

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmit = () => {
    const submissionData = {
      templateId: template._id,
      templateName: template.name,
      formData: formData,
      submittedAt: new Date()
    };
    onSubmit(submissionData);
    onClose();
  };

  const renderField = (field) => {
    switch (field.fieldType) {
      case 'measurement':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                step={field.precision || 0.1}
                min={field.min}
                max={field.max}
                value={formData[field.id] || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                className="form-input w-32"
                placeholder="0"
              />
              <span className="text-dark-300">{field.unit}</span>
              <span className="text-xs text-dark-400">({field.min}-{field.max} {field.unit})</span>
            </div>
          </div>
        );
        
      case 'condition-rating':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="flex space-x-1">
              {Array.from({ length: field.max }, (_, i) => i + 1).map(rating => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => handleFieldChange(field.id, rating)}
                  className={`text-2xl ${
                    formData[field.id] >= rating ? 'text-yellow-400' : 'text-dark-600'
                  }`}
                >
                  ⭐
                </button>
              ))}
            </div>
            {formData[field.id] && (
              <div className="text-sm text-primary-400">
                {field.labels[formData[field.id] - 1]}
              </div>
            )}
          </div>
        );
        
      case 'safety-check':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="flex space-x-4">
              {field.options.map(option => (
                <label key={option} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`safety-${field.id}`}
                    value={option}
                    checked={formData[field.id] === option}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    className="rounded"
                  />
                  <span className={`${
                    option === 'Pass' ? 'text-green-400' :
                    option === 'Fail' ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    {option === 'Pass' ? '✅' : option === 'Fail' ? '❌' : '⚠️'} {option}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );
        
      case 'photo-documentation':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="border-2 border-dashed border-dark-600 rounded-lg p-6 text-center hover:border-primary-500 transition-colors cursor-pointer">
              <div className="text-4xl mb-2">📷</div>
              <div className="text-white mb-1">Click to capture photo</div>
              <div className="text-sm text-dark-300">
                Max {field.maxPhotos} photos • Annotations: {field.annotations ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        );
        
      case 'tire-depth':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="grid grid-cols-2 gap-4">
              {field.positions.map(position => (
                <div key={position} className="space-y-1">
                  <label className="text-sm text-dark-300">{position}</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="20"
                      value={formData[`${field.id}_${position}`] || ''}
                      onChange={(e) => handleFieldChange(`${field.id}_${position}`, e.target.value)}
                      className="form-input w-20 text-sm"
                      placeholder="0.0"
                    />
                    <span className="text-xs text-dark-400">{field.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'fluid-level':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="flex space-x-4">
              {field.levels.map(level => (
                <label key={level} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`fluid-${field.id}`}
                    value={level}
                    checked={formData[field.id] === level}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    className="rounded"
                  />
                  <span className={`text-sm ${
                    level === 'OK' ? 'text-green-400' : 
                    level === 'Low' ? 'text-yellow-400' :
                    level === 'High' ? 'text-orange-400' : 'text-red-400'
                  }`}>{level}</span>
                </label>
              ))}
            </div>
          </div>
        );
        
      case 'damage-map':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="border border-dark-600 rounded-lg p-6 bg-dark-700 text-center">
              <div className="text-6xl mb-3">🚗</div>
              <div className="text-white mb-2">Interactive Damage Mapping</div>
              <div className="text-sm text-dark-300">Click on vehicle areas to mark damage</div>
            </div>
          </div>
        );
        
      case 'textarea':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="form-input w-full"
              rows={field.rows || 3}
              placeholder={field.placeholder}
            />
          </div>
        );
        
      case 'checkbox':
        return (
          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData[field.id] || false}
                onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                className="rounded"
              />
              <span className="form-label">
                {field.label}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </span>
            </label>
          </div>
        );
        
      case 'radio-group':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options.map(option => (
                <label key={option} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={`radio-${field.id}`}
                    value={option}
                    checked={formData[field.id] === option}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    className="rounded"
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>
        );
        
      case 'select':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="form-input w-full"
            >
              <option value="">Select an option...</option>
              {field.options?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        );
        
      case 'date-input':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="form-input w-full"
            />
          </div>
        );
        
      case 'time-input':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="time"
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="form-input w-full"
            />
          </div>
        );
        
      case 'signature':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="border border-dark-600 rounded-lg p-6 bg-dark-700 text-center cursor-pointer hover:bg-dark-600 transition-colors">
              <div className="text-3xl mb-2">✍️</div>
              <div className="text-white mb-1">Click to sign</div>
              <div className="text-sm text-dark-300">Digital signature capture</div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={formData[field.id] || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="form-input w-full"
              placeholder={field.placeholder || 'Enter text...'}
            />
          </div>
        );
    }
  };

  // Render fields from sections structure (new database format)
  const renderSectionField = (field) => {
    switch (field.fieldType) {
      case 'text':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={formData[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="form-input w-full"
              placeholder={field.placeholder || 'Enter text...'}
            />
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <textarea
              value={formData[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="form-input w-full"
              placeholder={field.placeholder || 'Enter details...'}
              rows={3}
            />
          </div>
        );

      case 'number':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={field.validation?.min}
                max={field.validation?.max}
                value={formData[field.key] || ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                className="form-input w-32"
                placeholder={field.placeholder || '0'}
              />
              {field.validation?.min !== undefined && field.validation?.max !== undefined && (
                <span className="text-xs text-dark-400">
                  ({field.validation.min}-{field.validation.max})
                </span>
              )}
            </div>
          </div>
        );

      case 'date':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={formData[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="form-input w-full"
            />
          </div>
        );

      case 'datetime':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="datetime-local"
              value={formData[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="form-input w-full"
            />
          </div>
        );

      case 'boolean':
      case 'switch':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              <input
                type="checkbox"
                checked={formData[field.key] || false}
                onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                className="form-checkbox mr-2"
              />
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              <input
                type="checkbox"
                checked={formData[field.key] || false}
                onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                className="form-checkbox mr-2"
              />
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option, index) => (
                <label key={index} className="flex items-center">
                  <input
                    type="radio"
                    name={field.key}
                    value={option}
                    checked={formData[field.key] === option}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className="form-radio mr-2"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );

      case 'dropdown':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <select
              value={formData[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="form-select w-full"
            >
              <option value="">{field.placeholder || 'Select an option...'}</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="border-2 border-dashed border-dark-600 rounded-lg p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
              <div className="text-4xl mb-2">📷</div>
              <div className="text-white mb-1">Click to upload image</div>
              <div className="text-sm text-dark-300">Or drag and drop</div>
            </div>
          </div>
        );

      case 'multipleImages':
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <div className="border-2 border-dashed border-dark-600 rounded-lg p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
              <div className="text-4xl mb-2">🖼️</div>
              <div className="text-white mb-1">Click to upload multiple images</div>
              <div className="text-sm text-dark-300">Or drag and drop</div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-2">
            <label className="form-label flex items-center">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={formData[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="form-input w-full"
              placeholder={field.placeholder || 'Enter text...'}
            />
          </div>
        );
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-black opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-dark-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="bg-dark-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl leading-6 font-medium text-white flex items-center">
                  {template.name}
                </h3>
                <p className="text-sm text-dark-300 mt-1">{template.description}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="px-2 py-1 bg-primary-900 text-primary-100 rounded text-xs">
                    {template.type}
                  </span>
                  <span className="px-2 py-1 bg-green-900 text-green-100 rounded text-xs">
                    🔧 {template.vehicleSystem}
                  </span>
                  <span className="px-2 py-1 bg-blue-900 text-blue-100 rounded text-xs">
                    ⚙️ {template.inspectionLevel}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <div className="space-y-6">
                {/* Handle both formFields (legacy) and sections/fields (new) structures */}
                {template.formFields?.map((field, index) => (
                  <div key={field.id} className="card p-4">
                    {renderField(field)}
                  </div>
                ))}
                
                {/* Render sections/fields structure */}
                {template.sections?.map((section) => (
                  <div key={section.key} className="space-y-4">
                    <div className="border-b border-dark-600 pb-2">
                      <h4 className="text-lg font-semibold text-white">{section.name}</h4>
                      {section.description && (
                        <p className="text-sm text-dark-400 mt-1">{section.description}</p>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      {section.fields?.map((field, fieldIndex) => (
                        <div key={field.key} className="card p-4">
                          {renderSectionField(field)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {(!template.formFields || template.formFields.length === 0) && 
                 (!template.sections || template.sections.length === 0) && (
                  <div className="text-center py-8 text-dark-400">
                    <div className="text-4xl mb-3">📋</div>
                    <div>No fields configured for this template</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="bg-dark-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-primary w-full sm:w-auto sm:ml-3"
            >
              💾 Save Inspection
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary mt-3 w-full sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Templates;